// src/components/MagicLinkLogin.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "../styles/MagicLinkLogin.css";
import { TailSpin } from "react-loader-spinner";

const MagicLinkLogin = ({ onClose, role = "viewer" }) => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [emailError, setEmailError] = useState("");

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError("Please enter your email address");
      return false;
    } else if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      return false;
    }

    setEmailError("");
    return true;
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (emailError) {
      validateEmail(e.target.value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post("/api/magic-link/public", {
        email,
        role,
        redirectPath: localStorage.getItem("returnPath") || "/",
        returnAccess: true,
      });

      if (response.data.success) {
        setSubmitted(true);
      } else {
        toast.error(response.data.message || "Failed to send access link");
      }
    } catch (error) {
      // In MagicLinkLogin.jsx, modify the catch block in handleSubmit:
      const errorMessage =
        error.response?.data?.message || "Failed to send access link";

      if (
        error.response?.status === 404 &&
        error.response?.data?.userNotFound
      ) {
        toast.error(
          "No account found with this email address. Please check and try again.",
          { autoClose: 5000 }
        );
      } else if (
        error.response?.status === 404 ||
        (error.response?.data?.message &&
          error.response?.data?.message.includes("not found"))
      ) {
        toast.error(
          "No shared reports found for this email. Please check and try again.",
          { autoClose: 5000 }
        );
      } else if (
        error.response?.status === 400 &&
        (error.response?.data?.message?.includes("Google Sign In") ||
          error.response?.data?.suggestGoogleAuth)
      ) {
        toast.info("Please use Google Sign In for this email address", {
          autoClose: 5000,
        });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div className="magic-link-modal-backdrop" onClick={onClose}>
      <div className="magic-link-container" onClick={handleModalClick}>
        <button
          className="close-button"
          onClick={onClose}
          aria-label="Close login form"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {!submitted ? (
          <>
            <div className="magic-link-header">
              <div className="magic-link-logo">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 4H4C2.89543 4 2 4.89543 2 6V18C2 19.1046 2.89543 20 4 20H20C21.1046 20 22 19.1046 22 18V6C22 4.89543 21.1046 4 20 4Z"
                    stroke="#0066FF"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M22 7L13.03 12.7C12.7213 12.8934 12.3643 13 12 13C11.6357 13 11.2787 12.8934 10.97 12.7L2 7"
                    stroke="#0066FF"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2>Access Shared Reports</h2>
              <p className="description">
                Enter your email to access reports that were previously shared
                with you
              </p>
            </div>

            <form onSubmit={handleSubmit} className="magic-link-form">
              <div className={`form-group ${emailError ? "error" : ""}`}>
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="your@email.com"
                  disabled={isSubmitting}
                  required
                  className={emailError ? "error" : ""}
                  aria-invalid={emailError ? "true" : "false"}
                />
                {emailError && (
                  <div className="error-message">{emailError}</div>
                )}
              </div>

              <button
                type="submit"
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="loading-state">
                    <TailSpin color="#FFFFFF" height={20} width={20} />
                    <span className="ml-2">Sending...</span>
                  </span>
                ) : (
                  "Send Access Link"
                )}
              </button>
            </form>

            <div className="note-container">
              <p className="note">
                For full app access, use Google Sign In below
              </p>
            </div>

            <div className="alternative-login">
              <div className="divider">
                <span>OR</span>
              </div>
              <button
                className="google-button"
                onClick={() =>
                  (window.location.href = "/api/auth?skipConsent=true")
                }
              >
                <img src="/g-logo.png" alt="Google" className="google-icon" />
                Sign in with Google
              </button>
            </div>
          </>
        ) : (
          <div className="success-message">
            <div className="success-icon">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M22 11.0799V11.9999C21.9988 14.1563 21.3005 16.2545 20.0093 17.9817C18.7182 19.7088 16.9033 20.9723 14.8354 21.5838C12.7674 22.1952 10.5573 22.1218 8.53446 21.3746C6.51167 20.6273 4.78465 19.2461 3.61096 17.4369C2.43727 15.6276 1.87979 13.4879 2.02168 11.3362C2.16356 9.18443 2.99721 7.13619 4.39828 5.49694C5.79935 3.85768 7.69278 2.71525 9.79619 2.24001C11.8996 1.76477 14.1003 1.9822 16.07 2.85986"
                  stroke="#28a745"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M22 4L12 14.01L9 11.01"
                  stroke="#28a745"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2>Check Your Email</h2>
            <p>Access link sent to:</p>
            <p className="email-sent">{email}</p>
            <p>Link expires in 24 hours</p>

            <button className="back-button" onClick={onClose}>
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MagicLinkLogin;
